import PropTypes from 'prop-types'
import { useState, useMemo, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Decimal } from 'decimal.js'
import { useDispatch } from 'react-redux'

import profileInfoActions from 'models/ProfileInfo/actions'

import RoundedButton from 'components/RoundedButton'

import { STRING, ENABLE_CRYPTO_REDEMPTION_FEES, EVENT_TYPE, SYMBOLS } from 'resources'
import { APP_URLS } from 'resources/urls'

import formatCrypto from 'utils/format-crypto'
import formatPoints, { convertPointsToAmount } from 'utils/format-points'
import formatCurrency from 'utils/format-currency'
import { sendAnalyticsEvent } from 'services/analytics'

import { CRYPTO_CONFIGS } from 'screens/Wallet/WalletRedemptions/CryptoRedemption/constants'
import NetworkFeeModalExplainer from './NetworkFeeModalExplainer'
import CryptoRedemptionSuccessModal from './CryptoRedemptionSuccessModal'

import {
  ConfirmationScreenContainer,
  ButtonContainer,
  ConfirmationScreenSection,
  ApproximateText,
  SectionText,
  SectionBoldText,
  TextIconSection,
  ExclamationButton,
  ExclamationIcon,
  NetworkFeeSection,
  NetworkFeeRowSection,
  WalletTitle,
  WalletText,
  CryptoToReceiveSection,
  CryptoToReceiveSectionText,
  CryptoToReceiveIconSection,
  ExclamationIconRed,
  ConfirmationWarningContainer,
  ConfirmationWarningDescription,
  ErrorMessage,
  Icon,
} from '../styles'

const ConfirmationScreen = ({
  crypto,
  points,
  pointsInCrypto,
  walletAddress,
  estimatedNetworkFeeCrypto,
  requestCryptoRedemption,
  isModalSuccessRedemptionOpen,
  setIsModalSuccessRedemptionOpen,
  isSubmitting,
  errorMessage,
  approximateValue,
  currency,
}) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const [isModalNetworkFeeOpen, setIsModalModalNetworkFeeOpen] = useState(false)

  const formattedPoints = useMemo(() => formatPoints(convertPointsToAmount(points), false, true), [points])
  const formattedPointsInCrypto = useMemo(() => formatCrypto(pointsInCrypto, crypto, true), [pointsInCrypto, crypto])
  const formattedNetworkFee = useMemo(
    () => formatCrypto(estimatedNetworkFeeCrypto, crypto, true),
    [estimatedNetworkFeeCrypto, crypto],
  )
  const formattedApproximateValue = useMemo(() => formatCurrency(approximateValue), [approximateValue])
  const isNetworkFeeGreaterThanPoints = useMemo(
    () => estimatedNetworkFeeCrypto.gt(pointsInCrypto),
    [estimatedNetworkFeeCrypto, pointsInCrypto],
  )

  const formattedTotalValue = useMemo(() => {
    const totalFeesAmount = ENABLE_CRYPTO_REDEMPTION_FEES ? Decimal.max(0, estimatedNetworkFeeCrypto) : new Decimal(0)
    return formatCrypto(pointsInCrypto.sub(totalFeesAmount), crypto, true)
  }, [pointsInCrypto, estimatedNetworkFeeCrypto, crypto])

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoConfirmationScreenView, {
      crypto,
      points: formattedPoints,
      walletAddress,
      pointsInCrypto: formattedPointsInCrypto,
      networkFeeInCrypto: formattedNetworkFee,
      isNetworkFeeGreaterThanPoints,
    })
  }, [
    crypto,
    walletAddress,
    formattedPoints,
    formattedPointsInCrypto,
    formattedNetworkFee,
    isNetworkFeeGreaterThanPoints,
  ])

  useEffect(() => {
    if (isModalSuccessRedemptionOpen) {
      dispatch(profileInfoActions.fetchProfileInfo.init())
    }
  }, [dispatch, isModalSuccessRedemptionOpen])

  const handleCloseNetworkFeeModal = () => {
    setIsModalModalNetworkFeeOpen(false)
  }
  const handleOpenNetworkFeeModal = () => {
    setIsModalModalNetworkFeeOpen(true)
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoConfirmationScreenNetworkFeeTooltipClick, { crypto })
  }

  const handleCloseRedemptionSuccessModal = () => {
    setIsModalSuccessRedemptionOpen(false)
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoRequestedScreenContinueClick, { crypto })
    history.push(APP_URLS.REDEMPTION_SOCIAL_SHARE)
  }

  const cryptoIcon = CRYPTO_CONFIGS[crypto]?.icon
  const cryptoTicker = STRING.crypto[crypto].ticker

  return (
    <>
      <NetworkFeeModalExplainer
        handleCloseNetworkFeeModal={handleCloseNetworkFeeModal}
        isModalNetworkFeeOpen={isModalNetworkFeeOpen}
      />
      <CryptoRedemptionSuccessModal
        handleCloseRedemptionSuccessModal={handleCloseRedemptionSuccessModal}
        isModalSuccessRedemptionOpen={isModalSuccessRedemptionOpen}
        walletAddress={walletAddress}
        totalAmountInCrypto={formattedTotalValue}
        crypto={crypto}
      />
      <ConfirmationScreenContainer>
        <WalletTitle>{STRING.cryptoRedeemScreen.confirmationScreen.receivingWalletAddress}</WalletTitle>
        <WalletText>{walletAddress}</WalletText>
        <ConfirmationScreenSection>
          <TextIconSection>
            <SectionBoldText>{STRING.cryptoRedeemScreen.confirmationScreen.ptsToRedeem}</SectionBoldText>
          </TextIconSection>
          <SectionText>
            {formattedPoints}
            <ApproximateText>
              {STRING.cryptoRedeemScreen.pointsConversionScreen.approximateText(formattedApproximateValue, currency)}
            </ApproximateText>
          </SectionText>
        </ConfirmationScreenSection>
        {ENABLE_CRYPTO_REDEMPTION_FEES && (
          <>
            <ConfirmationScreenSection>
              <TextIconSection>
                <SectionBoldText>{STRING.cryptoRedeemScreen.confirmationScreen.amount(cryptoTicker)}</SectionBoldText>
              </TextIconSection>
              <SectionText>{formattedPointsInCrypto}</SectionText>
            </ConfirmationScreenSection>
            <NetworkFeeSection>
              <NetworkFeeRowSection>
                <TextIconSection>
                  <SectionBoldText>{STRING.cryptoRedeemScreen.confirmationScreen.networkFee}</SectionBoldText>
                  <ExclamationButton onClick={handleOpenNetworkFeeModal}>
                    <ExclamationIcon />
                  </ExclamationButton>
                </TextIconSection>
                <SectionText>{`${SYMBOLS.minus} ${formattedNetworkFee}`}</SectionText>
              </NetworkFeeRowSection>
            </NetworkFeeSection>
          </>
        )}
        <CryptoToReceiveSection>
          <CryptoToReceiveIconSection>
            <Icon src={cryptoIcon} />
            <SectionBoldText>
              {STRING.cryptoRedeemScreen.confirmationScreen.cryptoToReceive(STRING.crypto[crypto].ticker)}
            </SectionBoldText>
          </CryptoToReceiveIconSection>
          <CryptoToReceiveSectionText>{formattedTotalValue}</CryptoToReceiveSectionText>
        </CryptoToReceiveSection>
        {isNetworkFeeGreaterThanPoints && (
          <ConfirmationWarningContainer>
            <ExclamationIconRed />
            <ConfirmationWarningDescription>
              {STRING.cryptoRedeemScreen.confirmationScreen.isFeeHigherThanAmountToReceive}
            </ConfirmationWarningDescription>
          </ConfirmationWarningContainer>
        )}
        <ButtonContainer>
          <RoundedButton
            onClick={requestCryptoRedemption}
            isLoading={isSubmitting}
            disabled={isNetworkFeeGreaterThanPoints}
          >
            {STRING.cryptoRedeemScreen.confirmationScreen.buttonText}
          </RoundedButton>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </ButtonContainer>
      </ConfirmationScreenContainer>
    </>
  )
}

ConfirmationScreen.propTypes = {
  crypto: PropTypes.string.isRequired,
  pointsInCrypto: PropTypes.instanceOf(Decimal).isRequired,
  estimatedNetworkFeeCrypto: PropTypes.instanceOf(Decimal).isRequired,
  approximateValue: PropTypes.instanceOf(Decimal).isRequired,
  currency: PropTypes.string.isRequired,
  points: PropTypes.number.isRequired,
  walletAddress: PropTypes.string.isRequired,
  requestCryptoRedemption: PropTypes.func.isRequired,
  isModalSuccessRedemptionOpen: PropTypes.bool.isRequired,
  setIsModalSuccessRedemptionOpen: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
}

export default ConfirmationScreen
